import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Alert,
  LinearProgress,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close,
  Upload,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  NavigateNext,
  NavigateBefore,
} from '@mui/icons-material';

const REQUIRED_COLUMNS = ['name', 'price', 'category_id'];
const OPTIONAL_COLUMNS = [
  'description',
  'sku',
  'original_price',
  'quantity',
  'condition',
  'location',
  'brand',
  'model',
  'year',
  'dimensions',
  'weight',
  'notes',
];

const ImportDialog = ({ open, onClose, onImport, sales, categories }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSale, setSelectedSale] = useState('');
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [errors, setErrors] = useState([]);

  const steps = ['Upload CSV', 'Map Columns', 'Preview & Confirm', 'Import'];

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      parseCSV(file);
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter((line) => line.trim());
      if (lines.length > 0) {
        const headers = lines[0].split(',').map((h) => h.trim());
        const data = lines.slice(1, 6).map((line) => {
          const values = line.split(',').map((v) => v.trim());
          return headers.reduce((obj, header, index) => {
            obj[header] = values[index] || '';
            return obj;
          }, {});
        });

        setCsvHeaders(headers);
        setCsvData(data);

        // Auto-map columns if they match
        const autoMapping = {};
        headers.forEach((header) => {
          const lowerHeader = header.toLowerCase().replace(/\s+/g, '_');
          if ([...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS].includes(lowerHeader)) {
            autoMapping[lowerHeader] = header;
          }
        });
        setColumnMapping(autoMapping);
      }
    };
    reader.readAsText(file);
  };

  const handleColumnMappingChange = (systemColumn, csvColumn) => {
    setColumnMapping((prev) => ({
      ...prev,
      [systemColumn]: csvColumn,
    }));
  };

  const validateMapping = () => {
    const errors = [];
    REQUIRED_COLUMNS.forEach((col) => {
      if (!columnMapping[col]) {
        errors.push(`Missing required column: ${col}`);
      }
    });
    setErrors(errors);
    return errors.length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedFile) {
      setErrors(['Please select a CSV file']);
      return;
    }
    if (activeStep === 0 && !selectedSale) {
      setErrors(['Please select a sale to import items into']);
      return;
    }
    if (activeStep === 1 && !validateMapping()) {
      return;
    }
    setErrors([]);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setErrors([]);
  };

  const handleImport = async () => {
    setImporting(true);
    setActiveStep(3);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('sale_id', selectedSale);

      // Add column mapping
      Object.keys(columnMapping).forEach((key) => {
        formData.append(`mapping[${key}]`, columnMapping[key]);
      });

      const result = await onImport(formData);
      setImportResult(result);
      setImporting(false);
    } catch (error) {
      setErrors([error.message || 'Import failed']);
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setSelectedSale('');
    setCsvData([]);
    setCsvHeaders([]);
    setColumnMapping({});
    setImporting(false);
    setImportResult(null);
    setErrors([]);
    onClose();
  };

  const getMappedPreviewData = () => {
    return csvData.map((row) => {
      const mapped = {};
      Object.keys(columnMapping).forEach((systemCol) => {
        const csvCol = columnMapping[systemCol];
        mapped[systemCol] = row[csvCol] || '';
      });
      return mapped;
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Items from CSV</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Sale</InputLabel>
              <Select
                value={selectedSale}
                label="Select Sale"
                onChange={(e) => setSelectedSale(e.target.value)}
              >
                {sales.map((sale) => (
                  <MenuItem key={sale.id} value={sale.id}>
                    {sale.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: 'action.hover',
              }}
            >
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Upload CSV File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a CSV file containing your inventory items
              </Typography>
              <Button variant="contained" component="label" startIcon={<Upload />}>
                Choose File
                <input type="file" hidden accept=".csv" onChange={handleFileChange} />
              </Button>
              {selectedFile && (
                <Box mt={2}>
                  <Chip label={selectedFile.name} color="primary" icon={<CheckCircle />} />
                  <Typography variant="caption" display="block" mt={1}>
                    {csvData.length} rows detected
                  </Typography>
                </Box>
              )}
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Required columns:</strong> name, price, category_id
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Optional columns:</strong> description, sku, original_price, quantity,
                condition, location, brand, model, year, dimensions, weight, notes
              </Typography>
            </Alert>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Map your CSV columns to system fields
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Match the columns in your CSV file with the corresponding fields in the system
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>System Field</strong>
                    </TableCell>
                    <TableCell>
                      <strong>CSV Column</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Required</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {REQUIRED_COLUMNS.map((col) => (
                    <TableRow key={col}>
                      <TableCell>{col}</TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={columnMapping[col] || ''}
                            onChange={(e) => handleColumnMappingChange(col, e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Not Mapped</em>
                            </MenuItem>
                            {csvHeaders.map((header) => (
                              <MenuItem key={header} value={header}>
                                {header}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Chip label="Required" color="error" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {OPTIONAL_COLUMNS.map((col) => (
                    <TableRow key={col}>
                      <TableCell>{col}</TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={columnMapping[col] || ''}
                            onChange={(e) => handleColumnMappingChange(col, e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Not Mapped</em>
                            </MenuItem>
                            {csvHeaders.map((header) => (
                              <MenuItem key={header} value={header}>
                                {header}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Chip label="Optional" variant="outlined" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Preview Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review the first few rows before importing
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {Object.keys(columnMapping)
                      .filter((key) => columnMapping[key])
                      .map((key) => (
                        <TableCell key={key}>
                          <strong>{key}</strong>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getMappedPreviewData().map((row, index) => (
                    <TableRow key={index}>
                      {Object.keys(columnMapping)
                        .filter((key) => columnMapping[key])
                        .map((key) => (
                          <TableCell key={key}>{row[key]}</TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 2 }}>
              Showing preview of first {csvData.length} rows. Total rows in file will be imported.
            </Alert>
          </Box>
        )}

        {activeStep === 3 && (
          <Box>
            {importing && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Importing Items...
                </Typography>
                <LinearProgress sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Please wait while we process your file
                </Typography>
              </Box>
            )}

            {!importing && importResult && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                {importResult.success ? (
                  <>
                    <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Import Successful!
                    </Typography>
                    <Typography variant="body1">
                      {importResult.created} items imported successfully
                    </Typography>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        {importResult.errors.length} rows had errors and were skipped
                      </Alert>
                    )}
                  </>
                ) : (
                  <>
                    <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Import Failed
                    </Typography>
                    <Typography variant="body1" color="error">
                      {importResult.error || 'An error occurred during import'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep < 3 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {activeStep > 0 && (
              <Button onClick={handleBack} startIcon={<NavigateBefore />}>
                Back
              </Button>
            )}
            {activeStep < 2 && (
              <Button variant="contained" onClick={handleNext} endIcon={<NavigateNext />}>
                Next
              </Button>
            )}
            {activeStep === 2 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleImport}
                startIcon={<Upload />}
              >
                Import Items
              </Button>
            )}
          </>
        )}
        {activeStep === 3 && !importing && (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
